// Agent session management — no password, no PIN, phone-number only auth
// Sessions stored in localStorage as mediqr_agent_session

export interface AgentSession {
  id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: 'agent' | 'union_admin';
  user_status: string;
  union_id: string | null;
  union_name: string | null;
  union_logo_url: string | null;
  state_id: string | null;
  state_name: string | null;
  last_login_at: string | null;
  session_created_at: string;
}

const SESSION_KEY = 'mediqr_agent_session';
const SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours

export function getSession(): AgentSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session: AgentSession = JSON.parse(raw);
    // Check session timeout
    const created = new Date(session.session_created_at).getTime();
    if (Date.now() - created > SESSION_TIMEOUT_MS) {
      clearSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function setSession(session: AgentSession): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_KEY);
}

export function isRegularAgent(session: AgentSession | null): boolean {
  return session?.role === 'agent';
}

export function isUnionAgent(session: AgentSession | null): boolean {
  return session?.role === 'union_admin';
}

export function getAgentDisplayName(session: AgentSession | null): string {
  if (!session) return 'Agent';
  return session.first_name || session.full_name?.split(' ')[0] || 'Agent';
}

export function getAgentUnionDisplay(session: AgentSession | null): string {
  if (!session) return '';
  return session.union_name || 'Independent Agent';
}